import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Ensure dayjs plugins are loaded
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Serializes a registration date/time picked in the competition account's
 * timezone into the UTC ISO string the backend stores.
 *
 * The picked `Dayjs` carries a wall-clock (what the user typed in the picker);
 * we reinterpret that wall-clock as being in `accountTimezone` and convert to
 * UTC. A missing timezone is treated as UTC.
 *
 * Example output: "2026-07-15T15:24:00Z"
 *
 * @param value - the Dayjs picked in the account timezone
 * @param accountTimezone - IANA timezone from the account address (e.g. 'America/New_York')
 * @returns UTC ISO string, or '' when value is empty
 */
export const registrationDateTimeToUtcIso = (
  value: Dayjs | null | undefined,
  accountTimezone?: string
): string => {
  if (!value || !value.isValid()) {
    return ''
  }

  const wallClock = value.format('YYYY-MM-DDTHH:mm:ss')
  const zoned = accountTimezone
    ? dayjs.tz(wallClock, accountTimezone)
    : dayjs.utc(wallClock)

  return zoned.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

/**
 * Parses a stored UTC ISO registration date/time into a `Dayjs` expressed in
 * the competition account's timezone, so the picker displays the same
 * wall-clock the user originally selected. A missing timezone is treated as UTC.
 *
 * @param isoString - stored UTC ISO string (e.g. '2026-07-15T15:24:00Z')
 * @param accountTimezone - IANA timezone from the account address
 * @returns Dayjs in the account timezone, or null when input is empty
 */
export const registrationDateTimeFromUtcIso = (
  isoString: string | null | undefined,
  accountTimezone?: string
): Dayjs | null => {
  if (!isoString) {
    return null
  }

  const asUtc = dayjs.utc(isoString)
  if (!asUtc.isValid()) {
    return null
  }

  return accountTimezone ? asUtc.tz(accountTimezone) : asUtc
}

/**
 * Formats a stored UTC ISO registration date/time for display in the account's
 * timezone, as date + 12-hour time (e.g. 'Apr 1, 2026 5:04 PM'). A missing
 * timezone is treated as UTC. Returns '' for empty input.
 *
 * @param isoString - stored UTC ISO string (e.g. '2026-04-02T00:04:00Z')
 * @param accountTimezone - IANA timezone from the account address
 */
export const formatRegistrationDateTime = (
  isoString: string | null | undefined,
  accountTimezone?: string
): string => {
  const zoned = registrationDateTimeFromUtcIso(isoString, accountTimezone)
  return zoned ? zoned.format('MMM D, YYYY h:mm A') : ''
}
